import type { ForecastContextSelectedOptions } from "@appretio/context-selector";
import { useContextWithSync } from "../useContextWithSync";
import type { AppliedFilters } from "./useSummaryHandlers";

/** Options for the summary context options sub-hook. */
interface UseSummaryContextOptionsParams {
  /** Resolved cube ID (version or undefined). */
  cubeId: string | undefined;
  /** Currently selected context filter values. */
  selectedContextOptions: ForecastContextSelectedOptions;
  /** Currently selected store status values. */
  selectedStoreStatuses: string[];
  /** Snapshot of the last applied filters (null before first apply). */
  appliedFilters: AppliedFilters | null;
  /** Currently selected producer type. */
  producer: string;
  /** Currently selected cube version ID. */
  version: string;
  /** Currently selected financial year. */
  year: string;
  /** Currently selected table dimension. */
  tableDim: string;
  /** Currently selected row dimension. */
  rowDim: string;
  /** Currently selected index producer. */
  idxProducer: string;
  /** Currently selected index cube version ID. */
  idxVersion: string;
  /** Currently selected index financial year. */
  idxYear: string;
}

/**
 * Manages context options loading, store status merging, and context sync
 * detection for the summary page.
 */
export function useSummaryContextOptions({
  cubeId,
  selectedContextOptions,
  selectedStoreStatuses,
  appliedFilters,
  producer,
  version,
  year,
  tableDim,
  rowDim,
  idxProducer,
  idxVersion,
  idxYear
}: UseSummaryContextOptionsParams) {
  return useContextWithSync({
    cubeIds: cubeId ? [cubeId] : [],
    selectedContextOptions,
    selectedStoreStatuses,
    appliedContext: appliedFilters?.context as
      | Record<string, string[] | undefined>
      | undefined,
    extraSyncFields: [
      {
        name: "producer",
        current: producer,
        applied: appliedFilters?.producer
      },
      { name: "version", current: version, applied: appliedFilters?.version },
      { name: "year", current: year, applied: appliedFilters?.year },
      {
        name: "table dim",
        current: tableDim,
        applied: appliedFilters?.tableDim
      },
      { name: "row dim", current: rowDim, applied: appliedFilters?.rowDim },
      {
        name: "index producer",
        current: idxProducer,
        applied: appliedFilters?.idxProducer
      },
      {
        name: "index version",
        current: idxVersion,
        applied: appliedFilters?.idxVersion
      },
      {
        name: "index year",
        current: idxYear,
        applied: appliedFilters?.idxYear
      }
    ]
  });
}
